// Walkthrough offline persistence (Sprint Item 7): audio chunks every 10s +
// the session timeline live in IndexedDB, so a dropped signal or a closed app
// never loses the recording. Chunks upload in seq order; (session, seq) is the
// idempotency key server-side — retries never duplicate.
const DB = "buildflow-wt";
let dbPromise = null;

function openWT() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => {
      const d = req.result;
      if (!d.objectStoreNames.contains("chunks")) {
        const st = d.createObjectStore("chunks", { keyPath: "cid", autoIncrement: true });
        st.createIndex("session", "session_id", { unique: false });
      }
      if (!d.objectStoreNames.contains("sessions")) {
        d.createObjectStore("sessions", { keyPath: "session_id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx(d, store, mode, fn) {
  return new Promise((resolve, reject) => {
    const t = d.transaction(store, mode);
    const r = fn(t.objectStore(store));
    t.oncomplete = () => resolve(r && r.result !== undefined ? r.result : true);
    t.onerror = () => reject(t.error);
  });
}

export async function wtSaveSession(s) {
  const d = await openWT();
  await tx(d, "sessions", "readwrite", (st) => st.put(s));
  return s;
}

export async function wtGetSession(session_id) {
  const d = await openWT();
  return tx(d, "sessions", "readonly", (st) => st.get(session_id));
}

export async function wtAddChunk(session_id, seq, blob) {
  const d = await openWT();
  await tx(d, "chunks", "readwrite", (st) => st.add({ session_id, seq, blob, uploaded: false, created: Date.now() }));
}

export async function wtPendingChunks(session_id = null) {
  const d = await openWT();
  const all = await tx(d, "chunks", "readonly", (st) => st.getAll());
  return (all || []).filter((c) => !c.uploaded && (!session_id || c.session_id === session_id));
}

export async function wtMarkChunkUploaded(cid) {
  const d = await openWT();
  const all = await tx(d, "chunks", "readonly", (st) => st.getAll());
  const c = (all || []).find((x) => x.cid === cid);
  if (c) await tx(d, "chunks", "readwrite", (st) => st.put({ ...c, uploaded: true }));
}

export async function wtClearSession(session_id) {
  const d = await openWT();
  const all = await tx(d, "chunks", "readonly", (st) => st.getAll());
  for (const c of (all || []).filter((x) => x.session_id === session_id)) {
    await tx(d, "chunks", "readwrite", (st) => st.delete(c.cid));
  }
  await tx(d, "sessions", "readwrite", (st) => st.delete(session_id));
}

// Drain pass (called on 'online' + after Done): upload pending chunks in
// order, then finish sessions whose chunks are all up.
const lastFinishTry = new Map(); // session_id → ts (Group A2: backoff, never a finish storm)

export async function flushWalkthroughs(api) {
  const pending = (await wtPendingChunks()).sort((a, b) => a.created - b.created);
  for (const c of pending) {
    const fd = new FormData();
    fd.append("seq", String(c.seq));
    fd.append("file", c.blob, `chunk-${c.seq}.webm`);
    await api.post(`/walkthroughs/${c.session_id}/chunk`, fd,
      { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
    await wtMarkChunkUploaded(c.cid);
  }
  const d = await openWT();
  const sessions = await tx(d, "sessions", "readonly", (st) => st.getAll());
  for (const s of sessions || []) {
    if (s.status !== "pending_finish") continue;
    if ((await wtPendingChunks(s.session_id)).length) continue;
    const lastTry = lastFinishTry.get(s.session_id) || 0;
    if (Date.now() - lastTry < 15000) continue; // Group A2: retry with backoff only
    lastFinishTry.set(s.session_id, Date.now());
    try {
      await api.post(`/walkthroughs/${s.session_id}/finish`, s.finishPayload || {}, { timeout: 60000 });
      await wtSaveSession({ ...s, status: "finished" });
      window.dispatchEvent(new CustomEvent("bf:walkthrough-ready",
        { detail: { sessionId: s.session_id, projectId: s.project_id } }));
    } catch (e) { /* next online pass retries */ }
  }
}

// P0-6/P0-4 support: sessions still being built (or stuck mid-recording).
export async function wtPendingFinishSessions() {
  try {
    const d = await openWT();
    const sessions = await tx(d, "sessions", "readonly", (st) => st.getAll());
    return (sessions || []).filter((s) => s.status === "pending_finish" || s.status === "recording");
  } catch { return []; }
}

// P0-6: the app was killed mid-recording — on next open, finish stuck
// sessions with whatever was captured (chunks + photo links live in IDB).
export async function recoverInterruptedWalkthroughs(api) {
  let recovered = 0;
  try {
    const stuck = (await wtPendingFinishSessions()).filter((s) => s.status === "recording");
    for (const s of stuck) {
      let photos = [];
      try {
        const { getQueued } = await import("@/lib/offlineQueue");
        const q = await getQueued({ includeUploaded: true });
        photos = q.filter((i) => i.kind === "photo" && i.walkthrough_id === s.session_id && i.client_id)
          .map((i) => ({ client_id: i.client_id, t: i.wt_t ?? null, taken_at: i.device_capture_ts || null, lat: i.lat ?? null, lng: i.lng ?? null }));
      } catch { /* queue unavailable */ }
      await wtSaveSession({ ...s, status: "pending_finish", finishPayload: s.finishPayload || { photos, areas: [], duration_s: 0 } });
      recovered += 1;
    }
  } catch { /* store unavailable */ }
  if (recovered) {
    try { window.dispatchEvent(new CustomEvent("bf:wt-recovered", { detail: { count: recovered } })); } catch { /* non-browser env */ }
    await flushWalkthroughs(api).catch(() => {});
  }
  return recovered;
}
